import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, ThemeProvider } from 'contexts';
import { ErrorBoundary } from 'components';

/**
 * AppProviders — composes every cross-cutting provider in one place so the
 * nesting order is explicit and App.js stays clean.
 *
 * Order (outer → inner):
 *   ErrorBoundary  catch render crashes anywhere below
 *   BrowserRouter  routing context (guards/hooks need this)
 *   ThemeProvider  theming, available app-wide
 *   AuthProvider   auth/session state, consumed by route guards
 *   Toaster        global toast portal (react-hot-toast)
 */
export default function AppProviders({ children }) {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            {children}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                // Toasts are portalled, so they can't inherit the Tailwind
                // token layer — the values are set literally here. Only
                // `fontFamily` follows the Apple type system; colour, radius
                // and shadow are the Indigo & Slate values.
                style: {
                  background: '#ffffff',
                  color: '#0F172A',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 8px 24px -8px rgba(16,24,40,0.16), 0 4px 8px -4px rgba(16,24,40,0.06)',
                  borderRadius: '0.875rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, system-ui, sans-serif',
                },
                success: { iconTheme: { primary: '#10B981', secondary: '#ffffff' } },
                error: { iconTheme: { primary: '#ef4444', secondary: '#ffffff' } },
              }}
            />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
